import CommHandler from './CommHandler';
import { getAuthHeader } from './utils';
import { broadcastNotificationUpdate } from './notificationUtils';

/**
 * Send a notification to users based on department criteria.
 * The actual recipient user IDs are determined by the backend based on the userDepartment parameter.
 *
 * @param {string} fromUserId - ID of the sender
 * @param {string} userDepartment - Department of the user triggering the notification
 * @param {string} message - Notification message
 * @param {string} [title] - Optional notification title
 * @param {number} quarter - Quarter (1-4)
 * @param {string} financialYear - Financial year in format YYYY-YYYY
 * @param {string} [department] - Target department name (PROJECT, BU, HR, RM, Company)
 * @param {string} [comment] - Comment for challenge notifications
 * @param {string} [projectId] - Project ID for project-related notifications
 * @param {number} [category_id] - Category ID for categorized notifications
 * @param {number} [designation_id] - Designation ID for role-based notifications
 * @returns {Promise<any>} API response containing the recipient user IDs (to_user_id)
 */
export const sendNotification = async ({
  fromUserId,
  userDepartment,
  message,
  title,
  quarter,
  financialYear,
  department,
  comment,
  projectId,
  category_id,
  designation_id,
}: {
  fromUserId: any;
  userDepartment: any;
  message: any;
  title?: any;
  quarter: any;
  financialYear: any;
  department?: any;
  comment?: any;
  projectId?: any;
  category_id?: any;
  designation_id?: any;
}) => {
  const response = await CommHandler.request({
    url: '/notification/send',
    method: 'POST',
    data: {
      user_id: fromUserId,
      userDepartment,
      message,
      quarter,
      financialYear,
      department,
      title,
      ...(comment ? { comment } : {}),
      ...(projectId ? { projectId } : {}),
      ...(category_id ? { category_id } : {}),
      ...(designation_id ? { designation_id } : {}),
    },
  });

  const rawToUserId =
    // Support both transformed responses with top-level `to_user_id`
    (response as any)?.to_user_id ??
    // ...and nested responses with `data.to_user_id`
    (response as any)?.data?.to_user_id;

  let toUserIds: string[] = [];
  if (Array.isArray(rawToUserId)) {
    toUserIds = rawToUserId;
  } else if (rawToUserId != null) {
    toUserIds = [rawToUserId];
  }

  if (toUserIds.length > 0) {
    broadcastNotificationUpdate(toUserIds);
  }
  return response;
};

/**
 * Get unread notifications for the current user.
 * @param {string} userId - ID of the user
 * @returns {Promise<any>} API response with unread notifications
 */
export const getUnreadNotifications = async (userId: string) => {
  try {
    const response = await CommHandler.request({
      url: '/notification/unread',
      method: 'GET',
      params: { user_id: userId },
      headers: getAuthHeader,
    });
    return response?.data || [];
  } catch (error) {
    console.error('Error fetching unread notifications:', error);
    return [];
  }
};

/**
 * Get notification count for the current user.
 * @param {string} userId - ID of the user
 * @returns {Promise<number>} Notification count
 */
export const getNotificationCount = async (userId: string) => {
  try {
    const response = await CommHandler.request({
      url: '/notification/count',
      method: 'GET',
      params: { user_id: userId },
      headers: getAuthHeader,
    });
    return response?.data?.count || 0;
  } catch (error) {
    console.error('Error fetching notification count:', error);
    return 0;
  }
};

/**
 * Mark notifications as read.
 * @param {number[]} notificationIds - Array of notification IDs to mark as read
 * @returns {Promise<any>} API response
 */
export const markNotificationsAsRead = async (notificationIds: number[]) => {
  try {
    const response = await CommHandler.request({
      url: '/notification/mark-as-read',
      method: 'PUT',
      data: { notification_ids: notificationIds },
      headers: getAuthHeader,
    });
    return response;
  } catch (error) {
    console.error('Error marking notifications as read:', error);
    throw error;
  }
};
